import type { Matrix } from "./encode.ts";
import type { EyeStyle, ModuleStyle, Paint, QrStyle } from "./style.ts";

/**
 * Draws a module grid as one self-contained SVG string.
 *
 * SVG is the source of truth for every format the app offers: the preview
 * inlines this string, the SVG download is this string, and the PNG/JPG/WebP
 * exports rasterise it (see export.ts). So it carries no external references —
 * a logo is embedded as a data: URL by the caller — and no CSS beyond its own
 * attributes.
 *
 * One unit in the coordinate system is one module, which keeps every shape
 * below readable: a dot has radius 0.45, a finder eye is 7 across.
 */
export interface RenderOptions {
  /** width/height attributes, in pixels. The viewBox is always in modules. */
  pixels?: number;
  /** Prefix for generated ids, so two codes on one page cannot collide. */
  idPrefix?: string;
}

export function renderSvg(matrix: Matrix, style: QrStyle, opts: RenderOptions = {}): string {
  const { size } = matrix;
  const margin = Math.max(0, Math.round(style.margin));
  const total = size + margin * 2;
  const px = opts.pixels ?? total * 10;
  const prefix = opts.idPrefix ?? "qr";

  const clear = logoClearing(matrix, style, margin, total);
  const isFinder = (row: number, col: number) =>
    (row < 7 && col < 7) || (row < 7 && col >= size - 7) || (row >= size - 7 && col < 7);
  const on = (row: number, col: number) =>
    row >= 0 &&
    col >= 0 &&
    row < size &&
    col < size &&
    matrix.dark(row, col) &&
    !isFinder(row, col) &&
    !clear(row, col);

  const body = modulePath(size, margin, style.modules, on);
  const eyes = eyePaths(size, margin, style.eyes);

  const defs: string[] = [];
  const moduleFill = paintRef(style.fill, `${prefix}-f`, defs);
  const eyeFill = style.eyeColor ? attr(style.eyeColor) : moduleFill;

  const parts: string[] = [];
  if (style.background) {
    parts.push(`<rect width="${total}" height="${total}" fill="${attr(style.background)}"/>`);
  }
  if (body) parts.push(`<path fill="${moduleFill}" d="${body}"/>`);
  if (eyes) parts.push(`<path fill="${eyeFill}" fill-rule="evenodd" d="${eyes}"/>`);
  parts.push(...logoMarkup(style, size, total, prefix));

  // Squares snapped to the device pixel grid look sharp at any preview size and
  // avoid hairline seams between neighbours; every other style is curved, and
  // snapping those would be visible as wobble.
  const rendering = style.modules === "square" ? "crispEdges" : "geometricPrecision";

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
    `width="${px}" height="${px}" viewBox="0 0 ${total} ${total}" ` +
    `shape-rendering="${rendering}" role="img" aria-label="QR code">` +
    (defs.length > 0 ? `<defs>${defs.join("")}</defs>` : "") +
    parts.join("") +
    `</svg>`
  );
}

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

function modulePath(
  size: number,
  margin: number,
  style: ModuleStyle,
  on: (row: number, col: number) => boolean,
): string {
  const out: string[] = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      if (!on(row, col)) continue;
      const x = col + margin;
      const y = row + margin;
      switch (style) {
        case "square":
          out.push(`M${n(x)} ${n(y)}h1v1h-1z`);
          break;
        case "rounded":
          out.push(roundedRect(x, y, 1, 1, [0.28, 0.28, 0.28, 0.28]));
          break;
        case "classy":
          out.push(roundedRect(x, y, 1, 1, [0.5, 0, 0.5, 0]));
          break;
        case "dots":
          out.push(circle(x + 0.5, y + 0.5, 0.45));
          break;
        case "diamond":
          out.push(
            `M${n(x + 0.5)} ${n(y)}L${n(x + 1)} ${n(y + 0.5)}L${n(x + 0.5)} ${n(y + 1)}L${n(x)} ${n(y + 0.5)}z`,
          );
          break;
        case "fluid": {
          // Round only the corners nothing is attached to, so runs of modules
          // melt into one rounded ribbon and a lone module becomes a dot.
          const up = on(row - 1, col);
          const down = on(row + 1, col);
          const left = on(row, col - 1);
          const right = on(row, col + 1);
          out.push(
            roundedRect(x, y, 1, 1, [
              !up && !left ? 0.5 : 0,
              !up && !right ? 0.5 : 0,
              !down && !right ? 0.5 : 0,
              !down && !left ? 0.5 : 0,
            ]),
          );
          break;
        }
      }
    }
  }
  return out.join("");
}

// ---------------------------------------------------------------------------
// Eyes
// ---------------------------------------------------------------------------

/** Outer ring radius, inner cut-out radius, and pupil radius, per style. */
const EYE_RADII: Record<EyeStyle, [number, number, number]> = {
  square: [0, 0, 0],
  rounded: [2, 1.4, 0.9],
  circle: [3.5, 2.5, 1.5],
  leaf: [3.5, 2.5, 1.5],
};

/** Which corners are rounded. Leaf keeps two of them sharp. */
function corners(style: EyeStyle, r: number): [number, number, number, number] {
  return style === "leaf" ? [r, 0, r, 0] : [r, r, r, r];
}

function eyePaths(size: number, margin: number, style: EyeStyle): string {
  const [outer, inner, pupil] = EYE_RADII[style];
  const origins: [number, number][] = [
    [margin, margin],
    [margin + size - 7, margin],
    [margin, margin + size - 7],
  ];
  const out: string[] = [];
  for (const [x, y] of origins) {
    // One path with a hole in it: the 7x7 frame, minus the 5x5 inside it.
    // fill-rule="evenodd" on the element turns the second subpath into the hole.
    out.push(roundedRect(x, y, 7, 7, corners(style, outer)));
    out.push(roundedRect(x + 1, y + 1, 5, 5, corners(style, inner)));
    out.push(roundedRect(x + 2, y + 2, 3, 3, corners(style, pupil)));
  }
  return out.join("");
}

// ---------------------------------------------------------------------------
// Logo
// ---------------------------------------------------------------------------

/** The plate rectangle in module coordinates, or null when there is no logo. */
function logoBox(
  style: QrStyle,
  size: number,
  total: number,
): { x: number; y: number; side: number; plate: number } | null {
  if (!style.logo) return null;
  const side = size * clamp(style.logo.scale, 0.08, 0.3);
  const plate = side + Math.max(0, style.logo.padding) * 2;
  return { x: (total - side) / 2, y: (total - side) / 2, side, plate };
}

/**
 * Modules the logo sits on. They are removed rather than covered so that the
 * plate is not needed for legibility — a transparent PNG with a logo still
 * reads, because the data under it was never drawn.
 */
function logoClearing(
  matrix: Matrix,
  style: QrStyle,
  margin: number,
  total: number,
): (row: number, col: number) => boolean {
  const box = logoBox(style, matrix.size, total);
  if (!box) return () => false;
  const half = box.plate / 2;
  const lo = total / 2 - half;
  const hi = total / 2 + half;
  return (row, col) => {
    const cx = col + margin + 0.5;
    const cy = row + margin + 0.5;
    return cx > lo && cx < hi && cy > lo && cy < hi;
  };
}

function logoMarkup(
  style: QrStyle,
  size: number,
  total: number,
  prefix: string,
): string[] {
  const logo = style.logo;
  const box = logoBox(style, size, total);
  if (!logo || !box) return [];

  const out: string[] = [];
  const radius = logo.shape === "circle" ? box.plate / 2 : logo.shape === "rounded" ? box.plate * 0.18 : 0;
  if (logo.plate) {
    out.push(
      `<rect x="${n(total / 2 - box.plate / 2)}" y="${n(total / 2 - box.plate / 2)}" ` +
        `width="${n(box.plate)}" height="${n(box.plate)}" rx="${n(radius)}" ry="${n(radius)}" ` +
        `fill="${attr(style.background ?? "#ffffff")}"/>`,
    );
  }
  const clipId = `${prefix}-logo`;
  const clipRadius = logo.shape === "circle" ? box.side / 2 : logo.shape === "rounded" ? box.side * 0.18 : 0;
  if (clipRadius > 0) {
    out.push(
      `<clipPath id="${clipId}"><rect x="${n(box.x)}" y="${n(box.y)}" width="${n(box.side)}" ` +
        `height="${n(box.side)}" rx="${n(clipRadius)}" ry="${n(clipRadius)}"/></clipPath>`,
    );
  }
  // xlink:href alongside href: Chrome and Firefox read the plain attribute, but
  // some rasterisers (and older Safari) still only honour the xlink one, and a
  // logo that vanishes in the exported PNG is worse than a duplicate attribute.
  out.push(
    `<image ${clipRadius > 0 ? `clip-path="url(#${clipId})" ` : ""}` +
      `x="${n(box.x)}" y="${n(box.y)}" width="${n(box.side)}" height="${n(box.side)}" ` +
      `preserveAspectRatio="xMidYMid meet" href="${attr(logo.href)}" xlink:href="${attr(logo.href)}"/>`,
  );
  return out;
}

// ---------------------------------------------------------------------------
// Paint
// ---------------------------------------------------------------------------

/** Returns the `fill` value, pushing a gradient definition when one is needed. */
function paintRef(paint: Paint, id: string, defs: string[]): string {
  if (paint.kind === "solid") return attr(paint.color);
  const stops =
    `<stop offset="0" stop-color="${attr(paint.color)}"/>` +
    `<stop offset="1" stop-color="${attr(paint.color2)}"/>`;
  if (paint.kind === "radial") {
    defs.push(`<radialGradient id="${id}">${stops}</radialGradient>`);
  } else {
    // objectBoundingBox coordinates: the unit square, rotated about its centre.
    const rad = (paint.angle * Math.PI) / 180;
    const dx = Math.cos(rad) / 2;
    const dy = Math.sin(rad) / 2;
    defs.push(
      `<linearGradient id="${id}" x1="${n(0.5 - dx)}" y1="${n(0.5 - dy)}" ` +
        `x2="${n(0.5 + dx)}" y2="${n(0.5 + dy)}">${stops}</linearGradient>`,
    );
  }
  return `url(#${id})`;
}

// ---------------------------------------------------------------------------
// Path helpers
// ---------------------------------------------------------------------------

/** Rounded rectangle as a subpath. Radii are [top-left, top-right, bottom-right, bottom-left]. */
function roundedRect(
  x: number,
  y: number,
  w: number,
  h: number,
  [tl, tr, br, bl]: [number, number, number, number],
): string {
  const max = Math.min(w, h) / 2;
  const a = Math.min(tl, max);
  const b = Math.min(tr, max);
  const c = Math.min(br, max);
  const d = Math.min(bl, max);
  if (a === 0 && b === 0 && c === 0 && d === 0) {
    return `M${n(x)} ${n(y)}h${n(w)}v${n(h)}h${n(-w)}z`;
  }
  return (
    `M${n(x + a)} ${n(y)}` +
    `H${n(x + w - b)}${arc(b, x + w, y + b)}` +
    `V${n(y + h - c)}${arc(c, x + w - c, y + h)}` +
    `H${n(x + d)}${arc(d, x, y + h - d)}` +
    `V${n(y + a)}${arc(a, x + a, y)}z`
  );
}

/** A quarter-circle corner, or a straight join when the radius is zero. */
function arc(r: number, x: number, y: number): string {
  return r === 0 ? `L${n(x)} ${n(y)}` : `A${n(r)} ${n(r)} 0 0 1 ${n(x)} ${n(y)}`;
}

function circle(cx: number, cy: number, r: number): string {
  return `M${n(cx - r)} ${n(cy)}a${n(r)} ${n(r)} 0 1 0 ${n(r * 2)} 0a${n(r)} ${n(r)} 0 1 0 ${n(-r * 2)} 0z`;
}

/** Three decimals is finer than any printer; more just inflates the file. */
function n(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Escapes a value going into a double-quoted attribute. */
function attr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
